import React from 'react';
import { LayoutDashboardIcon, UsersIcon, UserIcon, CalendarIcon, BriefcaseIcon, ShieldIcon, StethoscopeIcon, ContactIcon, TrendingUpIcon, SettingsIcon, LogOutIcon, FileTextIcon, Building2Icon, ClipboardListIcon, MessageSquareIcon, ChevronsLeftIcon, ChevronsRightIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { hasPermission } from '../../types';
interface SidebarProps {
  currentModule: string;
  onModuleChange: (module: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}
const menuItems = [{
  id: 'dashboard',
  label: 'Panel de Control',
  icon: LayoutDashboardIcon,
  permission: 'dashboard' as const
}, {
  id: 'patients',
  label: 'Pacientes',
  icon: UsersIcon,
  permission: 'patients' as const
}, {
  id: 'doctors',
  label: 'Doctores',
  icon: UserIcon,
  permission: 'doctors' as const
}, {
  id: 'appointments',
  label: 'Citas',
  icon: CalendarIcon,
  permission: 'appointments' as const
}, {
  id: 'clinical-history',
  label: 'Historial Clínico',
  icon: FileTextIcon,
  permission: 'clinicalHistory' as const
}, {
  id: 'services',
  label: 'Servicios',
  icon: BriefcaseIcon,
  permission: 'services' as const
}, {
  id: 'insurance',
  label: 'Seguros',
  icon: ShieldIcon,
  permission: 'insurance' as const
}, {
  id: 'specialties',
  label: 'Especialidades',
  icon: StethoscopeIcon,
  permission: 'specialties' as const
}, {
  id: 'contacts',
  label: 'Contactos',
  icon: ContactIcon,
  permission: 'contacts' as const
}, {
  id: 'companies',
  label: 'Empresas',
  icon: Building2Icon,
  permission: 'companies' as const
}, {
  id: 'opportunities',
  label: 'Oportunidades',
  icon: TrendingUpIcon,
  permission: 'opportunities' as const
}, {
  id: 'follow-up-notes',
  label: 'Notas de Seguimiento',
  icon: MessageSquareIcon,
  permission: 'followUpNotes' as const
}, {
  id: 'audit-log',
  label: 'Bitácora',
  icon: ClipboardListIcon,
  permission: 'auditLog' as const
}, {
  id: 'settings',
  label: 'Configuración',
  icon: SettingsIcon,
  permission: 'settings' as const
}];
export function Sidebar({
  currentModule,
  onModuleChange,
  isCollapsed,
  onToggleCollapse
}: SidebarProps) {
  const {
    logout,
    user
  } = useAuth();
  const visibleMenuItems = menuItems.filter(item => {
    if (!user) return false;
    return hasPermission(user.role, item.permission, 'read');
  });
  return <aside className={`h-screen bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col fixed left-0 top-0 z-30 transition-all duration-300 ease-in-out ${isCollapsed ? 'w-[68px]' : 'w-64'}`} data-id="element-69">
      {/* Logo */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center min-h-[65px]" data-id="element-70">
        <div className={`flex items-center gap-3 overflow-hidden ${isCollapsed ? 'justify-center w-full' : ''}`} data-id="element-71">
          <img src="https://cdn.magicpatterns.com/uploads/a741bfNsYBMGGZNxniTt4s/images.jpg" alt="Savidna Logo" className="w-9 h-9 rounded-full object-cover flex-shrink-0" data-id="element-72" />
          {!isCollapsed && <div className="min-w-0" data-id="element-73">
              <h1 className="text-lg font-bold text-gray-900 dark:text-white truncate" data-id="element-74">
                Savidna
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-75">
                CRM Médico
              </p>
            </div>}
        </div>
      </div>

      {/* Toggle Button - debajo del logo */}
      <button onClick={onToggleCollapse} className="mx-auto my-2 flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-500 dark:text-gray-400 transition-colors" title={isCollapsed ? 'Expandir menú' : 'Colapsar menú'} data-id="element-76">
        {isCollapsed ? <ChevronsRightIcon className="w-4 h-4" data-id="element-77" /> : <ChevronsLeftIcon className="w-4 h-4" data-id="element-78" />}
      </button>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2" data-id="element-79">
        <ul className="space-y-1 px-2" data-id="element-80">
          {visibleMenuItems.map(item => {
          const Icon = item.icon;
          const isActive = currentModule === item.id;
          return <li key={item.id} data-id="element-81">
                <button onClick={() => onModuleChange(item.id)} title={isCollapsed ? item.label : undefined} className={`
                    w-full flex items-center gap-3 rounded-lg
                    text-sm font-medium transition-colors relative group
                    ${isCollapsed ? 'px-0 py-2.5 justify-center' : 'px-3 py-2.5'}
                    ${isActive ? 'bg-sky-50 text-sky-600 dark:bg-sky-900/20 dark:text-sky-400' : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'}
                  `} data-id="element-82">
                  <Icon className="w-5 h-5 flex-shrink-0" data-id="element-83" />
                  {!isCollapsed && <span className="truncate" data-id="element-84">{item.label}</span>}
                  {isCollapsed && <span className="absolute left-full ml-2 px-2.5 py-1.5 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-md whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 z-50 shadow-lg" data-id="element-85">
                      {item.label}
                    </span>}
                </button>
              </li>;
        })}
        </ul>
      </nav>

      {/* Logout */}
      <div className="p-3 border-t border-gray-200 dark:border-gray-700" data-id="element-86">
        <button onClick={logout} title={isCollapsed ? 'Cerrar Sesión' : undefined} className={`w-full flex items-center gap-3 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors relative group ${isCollapsed ? 'px-0 py-2.5 justify-center' : 'px-3 py-2.5'}`} data-id="element-87">
          <LogOutIcon className="w-5 h-5 flex-shrink-0" data-id="element-88" />
          {!isCollapsed && <span data-id="element-89">Cerrar Sesión</span>}
          {isCollapsed && <span className="absolute left-full ml-2 px-2.5 py-1.5 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-md whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 z-50 shadow-lg" data-id="element-90">
              Cerrar Sesión
            </span>}
        </button>
      </div>
    </aside>;
}